// components/project/economic/StralciSummaryCard.jsx
// V2 — Card compatta stralci per la sezione "Controlli contrattuali".
// Sintesi (n° lavorazioni + valore + stato). Dettaglio espandibile riusa
// StralciByCategoryWidget (nessuna modifica alla logica/fonti dati).

import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Ban, ChevronDown, ChevronUp } from 'lucide-react';
import StralciByCategoryWidget from './StralciByCategoryWidget';

const formatCurrency = (v) => {
  const n0 = Number(v ?? 0);
  return (Math.round(n0 * 100) === 0 ? 0 : n0).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
  });
};

const StralciSummaryCard = ({ projectId, summary }) => {
  const [expanded, setExpanded] = useState(false);

  const { totalAmount, totalCount } = useMemo(() => {
    const byCat = summary?.stralci_by_category || {};
    let amount = 0, count = 0;
    Object.values(byCat).forEach((b) => {
      amount += Number(b?.total || 0);
      count += Number(b?.count || 0);
    });
    return { totalAmount: amount, totalCount: count };
  }, [summary]);

  const hasStralci = totalCount > 0;

  return (
    <Card
      className={`border ${hasStralci ? 'border-red-200 bg-red-50/40' : 'border-[#EDEDED]'}`}
      data-testid="stralci-summary-card"
      data-has-stralci={hasStralci ? '1' : '0'}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Ban className={`w-4 h-4 ${hasStralci ? 'text-red-600' : 'text-gray-400'}`} />
          <h4 className="text-sm font-bold uppercase tracking-wide text-[#3C3C3C]">Stralci contrattuali</h4>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[#737373] font-semibold">Lavorazioni stralciate</div>
              <div className={`text-lg font-bold ${hasStralci ? 'text-red-800' : 'text-slate-500'}`} data-testid="stralci-summary-count">
                {totalCount}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[#737373] font-semibold">Valore stralci</div>
              <div className={`text-lg font-bold ${hasStralci ? 'text-red-800' : 'text-slate-500'}`} data-testid="stralci-summary-amount">
                {formatCurrency(totalAmount)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${hasStralci ? 'bg-red-500 text-white' : 'bg-slate-200 text-slate-600'}`}
              data-testid="stralci-summary-status"
            >
              {hasStralci ? 'Presenti' : 'Nessuno stralcio'}
            </span>
            {hasStralci && (
              <button
                className="inline-flex items-center gap-1 text-xs text-[#0078C8] hover:underline"
                onClick={() => setExpanded((v) => !v)}
                data-testid="stralci-summary-toggle"
              >
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {expanded ? 'Riduci' : 'Dettaglio'}
              </button>
            )}
          </div>
        </div>

        {expanded && hasStralci && (
          <div className="mt-4 pt-4 border-t border-red-100" data-testid="stralci-summary-detail">
            <StralciByCategoryWidget projectId={projectId} summary={summary} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StralciSummaryCard;
